import { useContext, useMemo, useCallback } from 'react';
import {useDispatch} from 'react-redux';
import { setNavigationEvent } from '../store/actions/dataLayer';
import { definedValue } from '../utils';
import { getLayoutContext, PortalConfigContext } from '../config/portal';
import { getMessages } from '../tppServices/translations/messages';
import { useIntl } from 'react-intl';
import { useTPPServices } from '../tppServices/tppDIHooks';
import { getCurrentLocale } from '../utils/language';
import { generateSearchPath as generateEnginesSearchPath } from '../utils/urlHelpers/engines';
import { generateSearchPath } from '../utils/urlHelpers/boats';
import { getPathnameLanguage } from '../utils/language';
import { generateBlogPath } from '../utils/urlHelpers/blog';
import { getFSBOUrl } from '../utils/urlHelpers/fsbo';


export const generatePathFromRoute = (route, routesConstants) => {
  const routes = routesConstants;
  if (typeof route === 'object') {
      if (route.search) {
          if (route.type === 'engine') {
              return generateEnginesSearchPath({}, route.search, true);
          }
          return generateSearchPath({}, route.search, true);
      }
      if (route.article) {
          return `${getPathnameLanguage()}${route.article}`;
      }
      if (route.blog) {
          return generateBlogPath(route.blog);
      }
  }
  if (typeof route === 'string' && ['SELL_URL_ROOT', 'SELL_ENGINES_URL_ROOT'].indexOf(route) >= 0) {
      return getFSBOUrl(route);
  }
  return routes[route];
};

export const filterLinksByLanguage = (link, currentLanguage) => {
  if (link.languages && !link.languages.includes(currentLanguage)) {
    return false;
  }
  if (link.exceptLanguages && link.exceptLanguages.includes(currentLanguage)) {
    return false;
  }
  return true;
};

const createHeaderLinks = (headerLinks, t, messages, filterLinksByLanguage, currentLanguage, trackGenericClick, routesConstants) => {
  return headerLinks.map((link) => {
    const subLinks = link.links?.filter(link => filterLinksByLanguage(link, currentLanguage)) || [];
    return {
      name: t(messages.header[link.name]),
      route: generatePathFromRoute(link.route, routesConstants),
      onClick: () => trackGenericClick(link.event),
      additionalAttributes: {
        'data-e2e': 'nav-link',
      },
      links: subLinks.map((sublink) => {
        return {
          name: t(messages.header[sublink.name]),
          route: generatePathFromRoute(sublink.route, routesConstants),
          onClick: () => trackGenericClick(sublink.event),
          additionalAttributes: {
            rel: sublink.rel,
            target: sublink.target,
            'data-e2e': 'nav-sub-link',
          }
        };
      }),
    };
  });
};
const getHeaderLinks = (context, isTopNavigationV2Enabled) => {
  if (isTopNavigationV2Enabled) {
      return definedValue(context.linksV2, []);
  }
  return definedValue(context.links, []);
};

export const useNavLinksHelper = () => {
  const intl = useIntl();
  const { formatMessage: t } = intl;
  const messages = getMessages();
  const dispatch = useDispatch();
  const context = useContext(PortalConfigContext);
  const { routesConstants } = useTPPServices();

  const headerContext = definedValue(context.header, {});
  const navbarContext = getLayoutContext(headerContext?.layout, 'NavBar');
  const isTopNavigationV2Enabled = !!navbarContext?.enableTopNavigationV2;

  const currentLanguage = getCurrentLocale();

  const headerLinks = getHeaderLinks(navbarContext, isTopNavigationV2Enabled).filter(link => filterLinksByLanguage(link, currentLanguage));

  const trackGenericClick = useCallback((event) => {
    if (event) {
      setTimeout(() => {
        dispatch(setNavigationEvent(event));
      });
    }
  }, [dispatch]);

  const preparedNavLinks = useMemo(() => {
    return createHeaderLinks(
      headerLinks,
      t,
      messages,
      filterLinksByLanguage,
      currentLanguage,
      trackGenericClick,
      routesConstants
    );
  }, [headerLinks, trackGenericClick, currentLanguage]);

  return {
    preparedNavLinks
  };
};
